// The requests that can open a switch's console, sent once at a time (#2387).
//
// A switch has one console session (ADR-0115 decision 4a). A request that
// reads it — a plan on a model and build with a measured removed fate
// (ADR-0321), a read, an apply that reads again — holds that session until the
// switch has answered or `SWITCH_CLI_READ_DEADLINE` has passed, and every other
// request that would read the same switch is refused `409` meanwhile. On
// 2026-11-03 four `ports:plan` requests for one switch started in the same
// second from one tab, and the operator's own apply that followed was refused.
//
// **So a second request for the same operation on the same target, while the
// first is still in flight from this tab, is not sent: it joins the first.**
// That is `shared` (`writes.ts`), keyed by the operation and its target, so it
// joins only a request started since this tab's last write, as a read there
// does: a plan started before a save cannot show the save. For a plan or a
// read the answer is the one it would have got; for an apply it is the double
// click it was. A request for a different operation on the same switch is sent
// as before, and the server refuses whichever finds the console held. Queueing
// them is not this module's (#2387, Out).
//
// **Every call to an operation that can open a console goes through here**, and
// only from the modules `oneConsole.test.ts` names with the act that sends it.
// Moving around Zenith never opens a switch's console; an act does.
import { shared } from "./writes";

/** Sends `send` unless the same `operation` on the same `target` is in flight
 * from this tab since its last write, in which case it joins that one.
 * `target` is the switch's serial, or what the operation names where it reads
 * every switch it reaches: the site, the VLAN, the applied record. */
export function oneConsole<T>(target: string, operation: string, send: () => Promise<T>): Promise<T> {
  return shared(`console ${operation} ${target}`, send);
}
